import { DocumentTypeIdentifier } from '@/models/document-type-identifier.enum';
import { useI18n } from 'vue-i18n';

export function useDocumentHelpers() {
  const { t } = useI18n();

  function getDocumentFilename(documentNumber: string, orderNumber?: string): string {
    try {
      let suffix;

      // for return orders confirmation, documentNumber is case
      // number. Case number has no prefix identifier, hence this
      // check and a new document type identifier.
      if (!Number.isNaN(parseInt(documentNumber, 10))) {
        return [documentNumber, orderNumber, DocumentTypeIdentifier.CASE].filter((str) => !!str).join('_') + '.pdf';
      }

      const normalizedDocumentNumber = documentNumber.toLocaleUpperCase();

      for (const value in DocumentTypeIdentifier) {
        if (normalizedDocumentNumber.startsWith(value)) {
          suffix = (DocumentTypeIdentifier as Record<string, string>)[value];
          break;
        }
      }

      if (suffix === DocumentTypeIdentifier.SO) {
        return `${orderNumber}_${suffix}.pdf`;
      }

      return [normalizedDocumentNumber, orderNumber, suffix].filter((str) => !!str).join('_') + '.pdf';
    } catch (error) {
      console.error(
        'utilsMixin#getDocumentFilename: Failed to generate document filename',
        documentNumber,
        orderNumber,
        error,
      );

      return 'document.pdf';
    }
  }

  function openPDF(base64Data: string, documentNumber: string, orderNumber?: string) {
    const pdfWindow = window.open('');

    if (pdfWindow) {
      pdfWindow.document.write(
        `<a href='data:application/pdf;base64,${encodeURI(base64Data)}'
              download='${getDocumentFilename(documentNumber, orderNumber)}'
              style= "background-color: #26dfb3;
              color: #fff;
              display: block;
              text-align: center;
              font-size: 16px;
              text-decoration: none;
              position: absolute;
              bottom: 10px;
              right: 10px;
              padding: 15px 30px;
              border-radius: 50px;">
            ${t('global.download_button').toString()}
        </a>`,
      );
      pdfWindow.document.write('<style>body{margin: 0}</style>');
      pdfWindow.document.write(
        `<iframe border='0' width='100%' height='100%' src='data:application/pdf;base64, ${encodeURI(
          base64Data,
        )}'></iframe>`,
      );
    }
  }

  async function arrayBufferToSHA256(buffer: ArrayBuffer) {
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  async function generateFileSHA256(fileToConvert: File | string) {
    let arrayBuffer: ArrayBuffer;
    if (fileToConvert instanceof File) {
      arrayBuffer = await fileToConvert.arrayBuffer();
    } else {
      const response = await fetch(fileToConvert);
      arrayBuffer = await response.arrayBuffer();
    }
    return await arrayBufferToSHA256(arrayBuffer);
  }

  return { generateFileSHA256, openPDF, getDocumentFilename };
}
